import { Errors, ErrorType } from "../../types/Error";

// One build artifact serves every environment: the API base comes from
// /config.json, served next to the bundle. The build bakes in the same-origin
// default (public/config.json); the prod FE deploy overwrites the file on S3
// with the api host. Loaded once at bootstrap, before the app renders.
let API_URI = "/";

export async function loadAppConfig(): Promise<void> {
    try {
        const response = await fetch("/config.json");
        // A missing file comes back as index.html with a 200 (SPA fallback),
        // so the JSON parse below is the real existence check.
        const config = await response.json();
        if (typeof config.apiUri === "string" && config.apiUri) {
            API_URI = config.apiUri;
            console.log(`loadAppConfig: apiUri from config.json: ${API_URI}`);
        } else {
            console.log("loadAppConfig: config.json has no apiUri, using same-origin");
        }
    } catch (err) {
        // Fall back to same-origin, correct everywhere except prod, where a
        // deploy without config.json is broken either way.
        console.log(`loadAppConfig: no usable config.json (${err}), using same-origin`);
    }
}

export function geturl(uripath: string) {
    let updateUri = uripath.replace(/^\//, "");
    return API_URI + updateUri;
}

export async function deserializeAndValidate(response: Response) {
    if (!response.ok) {
        let errorBody;
        try {
            errorBody = await response.json();
        } catch (error: any) {
            throw Errors.SERVER_ERROR.createNew(response.statusText);
        }
        if (errorBody.isILError) {
            // A dead session can strike on any call. Send the browser to the
            // login screen instead of leaving each caller to surface a
            // cryptic error.
            if (
                errorBody.type === ErrorType.NOT_AUTHENTICATED &&
                window.location.pathname !== "/login"
            ) {
                window.location.assign("/login");
            }
            throw errorBody;
        } else {
            throw Errors.SERVER_ERROR.createNew(JSON.stringify(errorBody));
        }
    }
    // A success response that isn't JSON did not come from our API (e.g. a
    // proxy or CDN serving an HTML page). Fail with a presentable error
    // instead of leaking a JSON parse SyntaxError to the UI.
    const contentType = response.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
        throw Errors.SERVER_ERROR.createNew("Something went wrong. Please try again.");
    }
    return response.json();
}

export async function safeGET<T>(url: string, abort?: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        try {
            fetch(geturl(url), { credentials: "include", ...abort })
                .then(deserializeAndValidate)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error: any) {
            reject(error);
        }
    });
}

export async function safeTextGET(url: string, abort?: any): Promise<string> {
    const response = await fetch(geturl(url), { credentials: "include", ...abort });
    if (!response.ok) {
        return deserializeAndValidate(response); // always throws for a non-ok response
    }
    return response.text();
}

export async function safeFormPOST<T>(url: string, data: FormData, abort?: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        try {
            fetch(geturl(url), { credentials: "include", method: "POST", body: data, ...abort })
                .then(deserializeAndValidate)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error: any) {
            reject(error);
        }
    });
}

export async function safeFormPUT<T>(url: string, data: FormData, abort?: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        try {
            fetch(geturl(url), { credentials: "include", method: "PUT", body: data, ...abort })
                .then(deserializeAndValidate)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    reject(error);
                });
        } catch (error: any) {
            reject(error);
        }
    });
}

function safeOPWithData<T>(url: string, data: any, method: string, abort?: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        try {
            fetch(geturl(url), {
                method: method,
                credentials: "include",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
                ...abort,
            })
                .then(deserializeAndValidate)
                .then((obj) => resolve(obj))
                .catch((error) => reject(error));
        } catch (error: any) {
            reject(error);
        }
    });
}

export async function safePOST<T>(url: string, data: any, abort?: any): Promise<T> {
    return safeOPWithData(url, data, "POST", abort);
}
export async function safePUT<T>(url: string, data: any, abort?: any): Promise<T> {
    return safeOPWithData(url, data, "PUT", abort);
}

export async function safeDELETE<T>(url: string, data: any, abort?: any): Promise<T> {
    return safeOPWithData(url, data, "DELETE", abort);
}
